/**
 * CRM stavy: názov je objekt { sk, de, en } (z API / DB JSON).
 */

import { colorNameOrHexToHex } from './moduleSettings';
import { uiT } from './uiTranslate';

export const EMPTY_CRM_STATE_NAME = Object.freeze({ sk: '', de: '', en: '' });

/** Hodnoty pre Formik (cust007) – parita OTK ErrorTypeAdd / PreventionAdd. */
export function initialCrmStateNameFormValues(name) {
  return parseCrmStateName(name);
}

/** Payload pre API – objekt name so sk, en, de. */
export function crmStateNamePayload(name) {
  const p = parseCrmStateName(name);
  return {
    sk: String(p.sk ?? '').trim(),
    en: String(p.en ?? '').trim(),
    de: String(p.de ?? '').trim(),
  };
}

/** Default ako service_states (9ca3af). */
export const CRM_SETUP_STATE_DEFAULT_COLOR = '9ca3af';

/** Hodnota pre Prime ColorPicker (hex bez #). */
export function crmSetupStateColorForPicker(raw) {
  return colorNameOrHexToHex(raw, `#${CRM_SETUP_STATE_DEFAULT_COLOR}`).replace(/^#/, '');
}

/** Uloženie do DB — hex bez #, parita service_states. */
export function crmSetupStateColorForStorage(pickerValue) {
  const v = String(pickerValue ?? '').trim().replace(/^#/, '');
  if (/^[0-9A-Fa-f]{6}$/.test(v)) {
    return v.toLowerCase();
  }
  return CRM_SETUP_STATE_DEFAULT_COLOR;
}

/** Zobrazenie v tabuľke / swatch (# + hex alebo mapovanie starých názvov). */
export function crmSetupStateDisplayHex(raw) {
  return colorNameOrHexToHex(raw, `#${CRM_SETUP_STATE_DEFAULT_COLOR}`);
}

export function parseCrmStateName(name) {
  if (name == null) return { ...EMPTY_CRM_STATE_NAME };
  if (typeof name === 'string') {
    const trimmed = name.trim();
    if (trimmed.startsWith('{')) {
      try {
        const o = JSON.parse(name);
        if (o && typeof o === 'object' && !Array.isArray(o)) {
          return {
            sk: String(o.sk ?? ''),
            de: String(o.de ?? ''),
            en: String(o.en ?? ''),
          };
        }
      } catch {
        /* fallthrough */
      }
    }
    return { sk: name, de: name, en: name };
  }
  if (typeof name === 'object' && !Array.isArray(name)) {
    return {
      sk: String(name.sk ?? ''),
      de: String(name.de ?? ''),
      en: String(name.en ?? ''),
    };
  }
  return { ...EMPTY_CRM_STATE_NAME };
}

export function displayCrmStateName(name, locale) {
  const loc = ['sk', 'en', 'de'].includes(String(locale)) ? String(locale) : 'sk';
  const p = parseCrmStateName(name);
  return p[loc] || p.sk || p.en || p.de || '';
}

/** Kroky vykonania (crm_steps) – rovnaký tvar name JSON ako stavy. */
export const displayCrmStepName = displayCrmStateName;
export const crmStepNamePayload = crmStateNamePayload;
export const initialCrmStepNameFormValues = initialCrmStateNameFormValues;
export const withCrmStepNameCopySuffix = withCrmStateNameCopySuffix;

/** Pre lokálne fulltexty / toolbar search – všetky jazyky spojené. */
export function crmStateNameSearchBlob(name) {
  const p = parseCrmStateName(name);
  return [p.sk, p.de, p.en].filter(Boolean).join(' ');
}

export function withCrmStateNameCopySuffix(name) {
  const p = parseCrmStateName(name);
  const suf = uiT('CustomerLevels.CopySuffix');
  return {
    sk: p.sk ? `${p.sk}${suf}` : suf.trim(),
    de: p.de ? `${p.de}${suf}` : suf.trim(),
    en: p.en ? `${p.en}${suf}` : suf.trim(),
  };
}

/** Kanonické typy pre stĺpec „Zobraziť v“ (cust007 / cust023) – parita BE CrmApplicableTypes. */
export const CRM_SETUP_APPLICABLE_TYPE_CODES = Object.freeze([
  'notice',
  'inquiry',
  'acquisition',
]);

export function crmSetupApplicableTypeKey(code) {
  return `CrmState.Type.${String(code ?? '').toLowerCase()}`;
}

export function normalizeCrmSetupApplicableTypes(raw) {
  const map = {
    o: 'notice',
    notice: 'notice',
    oznam: 'notice',
    oznamy: 'notice',
    info: 'notice',
    announcement: 'notice',
    quote_request: 'inquiry',
    offer: 'inquiry',
    d: 'inquiry',
    inquiry: 'inquiry',
    dopyt: 'inquiry',
    a: 'acquisition',
    acquisition: 'acquisition',
    akvizicia: 'acquisition',
    akvizícia: 'acquisition',
  };
  const list = Array.isArray(raw) ? raw : (raw ? [raw] : []);
  const out = [];
  list.forEach((item) => {
    const code = map[String(item ?? '').trim().toLowerCase()] ?? String(item ?? '').trim().toLowerCase();
    if (CRM_SETUP_APPLICABLE_TYPE_CODES.includes(code) && !out.includes(code)) out.push(code);
  });
  return out;
}

export function formatCrmSetupApplicableTypes(rawTypes, t) {
  const types = normalizeCrmSetupApplicableTypes(rawTypes);
  if (!types.length) return '—';
  return types.map((code) => t(crmSetupApplicableTypeKey(code))).join('/');
}
